<template>
  <div>
    <v-card>
      <v-card-text class="px-8">
        <v-form ref="form" v-model="valid">
          <div class="d-flex align-center">
            <span
              class="subtitle1 mr-4"
              v-text="$t('network.secureWebGateway.customDnsFiltering.enableSwitcherTitle')"
            ></span>

            <v-switch
              v-model="localValue.dnsFilteringEnabled"
              class="dns-switch"
              color="primary lighten-1"
              density="compact"
              hide-details
              :disabled="loading || actionNotAllowed"
              :ripple="false"
              @click.prevent.stop.capture="onChangeDnsEnabledToggle"
            />
          </div>
          <span v-html="$t('network.secureWebGateway.dnsFilteringDescription')" />
        </v-form>
      </v-card-text>
    </v-card>
    <v-alert
      v-if="!localValue.dnsFilteringEnabled"
      :closable="false"
      type="warning"
      height="32"
      class="info-alert mt-4 py-0"
      density="compact"
      color="yellow-faint"
    >
      <div class="body2" v-html="$t(`network.secureWebGateway.dnsFilteringDisclaimer`)"></div>
    </v-alert>
    <v-card class="mt-4">
      <v-card-text class="px-8">
        <v-checkbox
          v-model="localValue.dnsAnonymousModeEnabled"
          :disabled="isFieldDisabled || loading"
          :ripple="false"
          class="mt-3"
          hide-details
        >
          <template #label>
            <div class="subtitle1">
              {{ $t("network.secureWebGateway.dnsResolverLabel") }}
            </div>
          </template>
        </v-checkbox>
        <div
          class="body2 ml-7 mb-6"
          :class="{
            'text-indigo-medium': !localValue.dnsAnonymousModeEnabled,
          }"
          v-text="$t('network.secureWebGateway.dnsResolverDescription')"
        />
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed, type PropType, type Ref, nextTick } from "vue";
import { useDialogsStore } from "@/_store/dialogs.module";
import { type DeviceLabel, useDevicesSettingsStore } from "@/_store/devices-settings.module.ts";
import isEqual from "lodash/isEqual";
import { SecureWebGatewayAction, VirtualOfficeAction } from "@/constants/network";
import { useI18n } from "vue-i18n";
import { SubscriptionModule } from "@/constants/workplaces";
import type { SecureWebGatewaySettings } from "@/_store/network/swg/secure-web-gateway.module.ts";
import { storeToRefs } from "pinia";
import { useVirtualOfficeStore } from "@/_store/network/virtual-office.module.ts";
import VirtualOfficeSetupModal from "@/components/modals/swg/VirtualOfficeSetupModal.vue";
import { ModalWidth } from "@/constants/modals.ts";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    settings: {
      type: Object as PropType<SecureWebGatewaySettings>,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["settings-updated"],
  setup(props, { emit }) {
    const form = ref();
    const valid = ref(true);
    const displayedLabels: Ref<DeviceLabel[]> = ref([]);
    const localValue = ref({ ...props.settings });
    const dialogsStore = useDialogsStore();
    const devicesSettingsStore = useDevicesSettingsStore();
    const virtualOfficeStore = useVirtualOfficeStore();
    const { vpnConnectionStatus } = storeToRefs(virtualOfficeStore);

    const { openDialog } = dialogsStore;
    const { labels } = storeToRefs(devicesSettingsStore);
    const { t } = useI18n();

    const actionNotAllowed = computed(() =>
      isWorkspaceFrozenOrActionRestricted(RolePermissionsScope.PROTECTION, SubscriptionModule.SWG)
    );

    const onChangeDnsEnabledToggle = (): void => {
      if (!localValue.value.dnsFilteringEnabled) {
        const modalConfig = {
          ...confirmationDialogsConfigConstructor({
            item: {},
            action: SecureWebGatewayAction.TOGGLE_CUSTOM_DNS_FILTERING,
            callback: () => {
              if (!vpnConnectionStatus.value.configured) {
                // TODO: NET-3533 - replace with a dedicated SWG VO setup endpoint once BE provides one
                nextTick(() => {
                  openDialog({
                    ...componentDialogsConfigConstructor({
                      component: VirtualOfficeSetupModal,
                      width: ModalWidth.LARGE,
                      action: VirtualOfficeAction.SETUP,
                      callback: async (locVal: {
                        region: { value: string; displayValue: string };
                        subnet: string;
                      }) => {
                        await virtualOfficeStore.setupVirtualOffice({
                          item: locVal,
                        });
                        emit("settings-updated", {
                          ...localValue.value,
                          dnsFilteringEnabled: true,
                          isBasicMode: false,
                          dnsBasicFilteringEnabled: false,
                        });
                      },
                      closeCallback: () => {
                        localValue.value.dnsFilteringEnabled = false;
                      },
                    }),
                  });
                });
              } else {
                localValue.value.dnsFilteringEnabled = true;
              }
            },
          }),
          header: {
            title: t(`modals.${SecureWebGatewayAction.TOGGLE_CUSTOM_DNS_FILTERING}.title`),
            titleClass: "text-red-dark",
            titleIcon: "warning",
          },
        };
        openDialog(modalConfig);
      } else {
        localValue.value.dnsFilteringEnabled = !localValue.value.dnsFilteringEnabled;
      }
    };

    const isFieldDisabled = computed(() => props.disabled || actionNotAllowed.value);

    watch(
      () => props.settings,
      (newVal) => {
        localValue.value = { ...newVal };
      },
      { immediate: true, deep: true }
    );

    watch(
      localValue,
      (newVal) => {
        if (
          (newVal.dnsFilteringEnabled === false || form.value?.validate()) &&
          !isEqual(newVal, props.settings)
        ) {
          emit("settings-updated", {
            ...newVal,
            isBasicMode: false,
            dnsBasicFilteringEnabled: false,
          });
        }
      },
      { deep: true }
    );

    return {
      displayedLabels,
      labels,
      form,
      valid,
      localValue,
      actionNotAllowed,
      onChangeDnsEnabledToggle,
      isFieldDisabled,
    };
  },
});
</script>

<style scoped lang="scss">
.color-preview {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  border-radius: 2px !important;
}

.dns-switch {
  position: relative;
  top: 1px;
}

.label-popup-block span {
  flex: unset !important;
}

.disabled-text {
  color: rgb(var(--v-theme-indigo-medium));
}

.info-alert {
  background-color: rgb(var(--v-theme-indigo-pale));

  .body2 {
    color: rgb(var(--v-theme-yellow-base));
  }
}

:deep(*) {
  .v-input--checkbox .v-input__slot {
    margin-bottom: 0 !important;
  }

  .v-input--reverse .v-input__slot {
    .v-label {
      order: -1;
      flex-grow: 0 !important;
    }
  }
  .v-alert__prepend {
    align-self: end;
    .icon-warning-xs.v-icon {
      font-size: 24px !important;
      &:before {
        color: rgb(var(--v-theme-yellow-base));
      }
    }
  }
}
</style>
